import React, { useEffect, useRef } from 'react'

/**
 * ShadowSunLight — the directional shadow-casting "sun" (the caster half of the
 * old ContactShadow). It mainly populates a shadow map; intensity is kept low
 * so the IBL still owns the overall look. Its shadow lands on the shared
 * <ShadowCatcher> plane.
 *
 * Split out from the catcher plane deliberately: mounting the light
 * independently means toggling the directional light removes ONLY its shadow,
 * while the shared plane persists for any other caster (e.g. the spotlight).
 *
 * Shadow-map quality (mapSize/frustum/bias) belongs to THIS light's shadow map,
 * so those knobs live with the directional light in the tune panel. The
 * imperative effect mirrors three's requirement that resizing the shadow map
 * disposes the old render targets and moving the ortho frustum re-runs
 * updateProjectionMatrix() — see the ContactShadow history for the mapSize bug.
 */
export default function ShadowSunLight({
  lightPos = [2, 5, 2],
  intensity = 0,
  color = '#ffffff',
  mapSize = 2048,
  cameraSize = 3,
  radius = 12,
  blurSamples = 30,
  bias = -0.0005,
}) {
  const lightRef = useRef(null)
  const prevMapSizeRef = useRef(mapSize)
  useEffect(() => {
    const light = lightRef.current
    if (!light) return
    const cam = light.shadow.camera
    cam.left = -cameraSize
    cam.right = cameraSize
    cam.top = cameraSize
    cam.bottom = -cameraSize
    cam.updateProjectionMatrix()
    if (prevMapSizeRef.current !== mapSize) {
      prevMapSizeRef.current = mapSize
      light.shadow.mapSize.set(mapSize, mapSize)
      // Drop old target(s) so three re-allocates at the new resolution. VSM
      // needs BOTH map + mapPass disposed or the two-pass blur desyncs.
      if (light.shadow.map) { light.shadow.map.dispose(); light.shadow.map = null }
      if (light.shadow.mapPass) { light.shadow.mapPass.dispose(); light.shadow.mapPass = null }
    }
    light.shadow.bias = bias
  }, [mapSize, cameraSize, bias])

  return (
    <directionalLight
      ref={lightRef}
      position={lightPos}
      color={color}
      intensity={intensity}
      castShadow
      shadow-mapSize-width={mapSize}
      shadow-mapSize-height={mapSize}
      shadow-camera-near={0.1}
      shadow-camera-far={20}
      shadow-camera-left={-cameraSize}
      shadow-camera-right={cameraSize}
      shadow-camera-top={cameraSize}
      shadow-camera-bottom={-cameraSize}
      shadow-bias={bias}
      shadow-radius={radius}
      shadow-blurSamples={blurSamples}
    />
  )
}
